import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";

import { away, useSession } from "@/auth/session";
import { Alert, Button, Field, Input, Modal, PasswordInput } from "@/components/ui";
import { session } from "@/lib/api";
import {
  discardOtherUser,
  reauthMessage,
  reauthWithMfa,
  reauthWithPassword,
  type ReauthStep,
} from "@/lib/reauth";
import type { ExpiryReason, SessionWarning } from "@/lib/session";

/**
 * Session expiry over the page (PHP-3322, QB-023, QA-30). Rendered once at the app root, beside the
 * router, so nothing underneath ever unmounts: the warning before the end, the re-login prompt after
 * it, and a banner while the prompt is put off. Requests that got a 401 wait for the prompt and are
 * replayed after the same user signs in again; failed reads are refetched.
 */
export function SessionGuard() {
  const s = useSession();
  const qc = useQueryClient();
  useEffect(
    () =>
      session.onReauthenticated(() => {
        void qc.refetchQueries({ type: "active", predicate: (q) => q.state.status === "error" });
      }),
    [qc],
  );
  if (s.status === "signed_in" && s.warning) return <ExpiryWarning warning={s.warning} />;
  if (s.status !== "expired") return null;
  if (s.switchedUser) return <SwitchedUser />;
  if (s.dismissed) return <SignedOutBanner />;
  return <ReloginDialog reason={s.reason} email={s.user.email} />;
}

/** Sign out for good: the one way the page is left. A failed logout call still signs out. */
function useSignOut() {
  const qc = useQueryClient();
  return () => {
    // like the sidebar: drop the caches only once the session is gone, so nothing refetches as the old user
    void session
      .signOut()
      .catch(() => undefined)
      .finally(() => qc.clear());
  };
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** 105_000 → "1:45". */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function ExpiryWarning({ warning }: { warning: SessionWarning }) {
  const signOut = useSignOut();
  const now = useNow();
  const stayRef = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  // A hard end can't be pushed back: once acknowledged, stop interrupting.
  const [acknowledged, setAcknowledged] = useState<number | null>(null);
  const { endsAt, extendable } = warning;
  if (!extendable && acknowledged === endsAt) return null;

  const stay = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      if ((await session.staySignedIn()) === "unreachable")
        setProblem("We couldn't reach the sign-in service. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };
  // Escape and the close button do the harmless thing: stay signed in (or just acknowledge).
  const close = extendable ? () => void stay() : () => setAcknowledged(endsAt);
  const left = formatCountdown(endsAt - now);

  return (
    <Modal
      open
      onClose={close}
      title="Your session is about to end"
      initialFocus={stayRef}
      footer={
        extendable ? (
          <>
            <Button
              variant="secondary"
              onClick={signOut}
              disabledReason={busy ? "Wait while we keep you signed in" : undefined}
            >
              Sign out
            </Button>
            <Button ref={stayRef} onClick={() => void stay()} loading={busy}>
              Stay signed in
            </Button>
          </>
        ) : (
          <Button ref={stayRef} onClick={close}>
            OK
          </Button>
        )
      }
    >
      <p>
        {extendable ? "You'll be signed out in " : "Your sign-in ends in "}
        {/* A timer is not a live region: it is read on request, not every second. */}
        <strong role="timer" aria-label={`${left} remaining`}>
          {left}
        </strong>
        {extendable ? "." : " and can't be extended any further."}
      </p>
      <p className="text-sm text-fg-muted">
        {extendable
          ? "Anything you've typed stays on the page either way: if the session ends you can sign in again without losing it."
          : "Save what you're working on. When it ends you'll be asked to sign in again here, and this page and anything you've typed will be kept."}
      </p>
      {problem && <Alert tone="danger">{problem}</Alert>}
    </Modal>
  );
}

const REASON: Record<ExpiryReason, string> = {
  ended: "Your session has ended.",
  revoked: "Your session was ended, for example by signing out on another device or changing your password.",
  "signed-out-elsewhere": "You signed out in another tab.",
};

type Step =
  { kind: "password" } | { kind: "mfa"; challengeToken: string } | { kind: "other-user"; email: string };

function ReloginDialog({ reason, email: accountEmail }: { reason: ExpiryReason; email: string }) {
  const signOut = useSignOut();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>({ kind: "password" });
  const [email, setEmail] = useState(accountEmail);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const apply = (r: ReauthStep) => {
    // "done": the session store closes this dialog and replays the waiting requests.
    if (r.step === "mfa") setStep({ kind: "mfa", challengeToken: r.challengeToken });
    else if (r.step === "other-user") setStep({ kind: "other-user", email: r.email });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || step.kind === "other-user") return;
    setBusy(true);
    setError(undefined);
    try {
      apply(
        step.kind === "mfa"
          ? await reauthWithMfa(step.challengeToken, code, useRecovery)
          : await reauthWithPassword(email, password),
      );
    } catch (err) {
      setError(reauthMessage(err));
    } finally {
      setBusy(false);
      setPassword("");
      setCode("");
    }
  };

  const backToPassword = async () => {
    setBusy(true);
    try {
      await discardOtherUser();
    } finally {
      setBusy(false);
      setError(undefined);
      setStep({ kind: "password" });
    }
  };

  if (step.kind === "other-user") {
    return (
      <Modal
        open
        onClose={() => undefined}
        dismissible={false}
        title="That's a different account"
        footer={
          <>
            <Button variant="secondary" onClick={() => void backToPassword()} loading={busy}>
              Back
            </Button>
            <Button
              variant="danger"
              onClick={() => away.go("/")}
              disabledReason={busy ? "Wait while we go back" : undefined}
            >
              Discard this page and continue
            </Button>
          </>
        }
      >
        <p>
          You signed in as <strong>{step.email}</strong>, but this page belongs to{" "}
          <strong>{accountEmail}</strong>.
        </p>
        <p className="text-sm text-fg-muted">
          The page can't carry on as someone else. Go back and sign in as {accountEmail} to keep what you
          typed, or continue as {step.email}: the page reloads and unsaved changes are lost.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={() => undefined}
      dismissible={false}
      title="Sign in to continue"
      initialFocus={passwordRef}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={signOut}
            disabledReason={busy ? "Wait while we sign you in" : undefined}
          >
            Sign out
          </Button>
          <span className="flex-1" />
          <Button
            variant="secondary"
            onClick={() => session.postponeReauth()}
            disabledReason={busy ? "Wait while we sign you in" : undefined}
          >
            Not now
          </Button>
          <Button type="submit" loading={busy}>
            {step.kind === "mfa" ? "Verify" : "Sign in"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-fg-muted">
        {REASON[reason]} Sign in again to carry on: this page and anything you typed are kept.
      </p>
      {error && <Alert tone="danger">{error}</Alert>}
      {step.kind === "mfa" ? (
        <>
          <Field label={useRecovery ? "Recovery code" : "Authentication code"}>
            {(p) => (
              <Input
                {...p}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode={useRecovery ? "text" : "numeric"}
                autoComplete="one-time-code"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- a new step inside an open dialog
                autoFocus
                required
                maxLength={useRecovery ? 32 : 6}
                pattern={useRecovery ? undefined : "[0-9]{6}"}
              />
            )}
          </Field>
          <button
            type="button"
            className="text-sm text-brand-700 hover:underline dark:text-brand-500"
            onClick={() => {
              setUseRecovery((v) => !v);
              setCode("");
            }}
          >
            {useRecovery ? "Use authenticator code" : "Use a recovery code"}
          </button>
        </>
      ) : (
        <>
          <Field label="Email">
            {(p) => (
              <Input
                {...p}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            )}
          </Field>
          <Field label="Password">
            {(p) => (
              <PasswordInput
                {...p}
                ref={passwordRef}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            )}
          </Field>
          <p className="text-sm text-fg-muted">
            Signed in with Google or GitHub?{" "}
            <a
              href="/login"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 hover:underline dark:text-brand-500"
            >
              Sign in in a new tab
            </a>
            . This page carries on by itself once you're signed in there.
          </p>
        </>
      )}
    </Modal>
  );
}

/** Another tab signed in as someone else: this page can only be reset. */
function SwitchedUser() {
  const reloadRef = useRef<HTMLButtonElement>(null);
  return (
    <Modal
      open
      onClose={() => undefined}
      dismissible={false}
      title="Signed in as someone else"
      initialFocus={reloadRef}
      footer={
        <Button ref={reloadRef} onClick={() => away.go("/")}>
          Reload as the new account
        </Button>
      }
    >
      <p>A different account signed in to CortexGrip in another tab.</p>
      <p className="text-sm text-fg-muted">
        This page belonged to the previous account, so it can't continue. Reloading discards unsaved changes.
      </p>
    </Modal>
  );
}

/** The prompt was put off: the page stays readable (copy work out), every save asks again. */
function SignedOutBanner() {
  return createPortal(
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-50 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface px-4 py-3 text-sm text-fg shadow-lg"
    >
      <span>You're signed out. Your unsaved work stays on this page until you leave it.</span>
      <Button size="sm" onClick={() => session.resumeReauth()}>
        Sign in
      </Button>
    </div>,
    document.body,
  );
}
